import React, { useRef, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import styles from './Popover.module.css';

interface PopoverProps {
  isOpen: boolean;
  onClose: () => void;
  anchor: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

const Popover: React.FC<PopoverProps> = ({ isOpen, onClose, anchor, children, className = '' }) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const outsideAnchor = !anchorRef.current?.contains(target);
      const outsidePanel = !panelRef.current?.contains(target);
      if (outsideAnchor && outsidePanel) onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !anchorRef.current) return;
    // Measure the trigger element (last child = the button), not the full wrapper which may include a label above it
    const trigger = (anchorRef.current.lastElementChild ?? anchorRef.current) as HTMLElement;
    const rect = trigger.getBoundingClientRect();
    setPanelStyle({
      position: 'fixed',
      top: rect.bottom + 6,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
    });
  }, [isOpen]);

  return (
    <>
      <div ref={anchorRef} className="w-full">
        {anchor}
      </div>
      {isOpen && ReactDOM.createPortal(
        <div ref={panelRef} style={panelStyle} className={`${styles.panel} ${className}`}>
          {children}
        </div>,
        document.body
      )}
    </>
  );
};

export default Popover;
